import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Chip,
  Button,
  Stack,
  useTheme,
  alpha
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Description as DocumentIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  PeopleAlt as PeopleIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';

// Reusable StatCard component
const StatCard = ({ icon, title, value, change, trend, color }) => {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
        },
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '4px', bgcolor: color }} />
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="700" sx={{ mb: 1 }}>
              {value?.toLocaleString()}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {trend === 'up' ? (
                <TrendingUpIcon fontSize="small" sx={{ color: theme.palette.success.main, mr: 0.5 }} />
              ) : (
                <AssessmentIcon fontSize="small" sx={{ color: theme.palette.error.main, mr: 0.5 }} />
              )}
              <Typography variant="body2" color={trend === 'up' ? 'success.main' : 'error.main'} fontWeight="medium">
                {change}% {trend === 'up' ? 'increase' : 'decrease'}
              </Typography>
            </Box>
          </Box>

          <Avatar
            sx={{
              bgcolor: alpha(color, 0.1),
              color: color,
              width: 48,
              height: 48,
              borderRadius: 2,
              marginTop: "30px"
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};

const SalesManagerDashboard = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({
    leads: { value: 2, change: 10, trend: 'up' },
    opportunities: { value: 0, change: 5, trend: 'up' },
    closedDeals: { value: 0, change: -2, trend: 'down' },
    pendingFollowUps: { value: 0, change: 4, trend: 'up' }
  });

  const [contractStats, setContractStats] = useState({
    teamContracts: 0,
    personalContracts: 0,
    pendingContracts: 0,
    totalValue: 0
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userFromStorage = localStorage.getItem('user');
        if (!userFromStorage) {
          console.error('No user data in localStorage');
          return;
        }

        const parsedUser = JSON.parse(userFromStorage);
        if (!parsedUser?.id) {
          console.error('No user ID found');
          return;
        }

        const token = localStorage.getItem('authToken');
        const headers = token ? {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        } : {};

        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/user/${parsedUser.id}`, { headers });

        if (!response.ok) {
          const errorData = await response.text();
          console.error('API Error:', {
            status: response.status,
            statusText: response.statusText,
            errorData,
          });
          throw new Error(`API request failed with status ${response.status}`);
        }

        const data = await response.json();
        setUserData(data || parsedUser); // fallback to parsedUser if data is empty

      } catch (error) {
        console.error('Fetch error:', error);
        // Fallback to localStorage data
        const fallbackUser = localStorage.getItem('user');
        if (fallbackUser) {
          setUserData(JSON.parse(fallbackUser));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []); // Runs only once on mount
  
  useEffect(() => {
    const fetchContractStats = async () => {
      try {
        const userFromStorage = localStorage.getItem('user');
        const userData = JSON.parse(userFromStorage);
        
        // Get auth token
        const token = localStorage.getItem('authToken');
        if (!token) {
          throw new Error('No auth token found');
        }

        // Fetch personal contracts first
        const myContractsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/customer-contracts`);
        const allContracts = myContractsResponse.data.data || [];
        const personalContracts = allContracts.filter(c => c.created_by === userData.name);
        
        // Fetch team contracts
        const teamResponse = await axios.get(`${process.env.REACT_APP_API_URL}/users/my-sales-teamcontracts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
          // Extract team contracts only if response was successful
        let teamContracts = [];
        if (teamResponse.data.success && teamResponse.data.salesTeam) {
          teamContracts = teamResponse.data.salesTeam.reduce((acc, member) => {
            // Only add contracts where the created_by matches the team member's name
            const memberContracts = member.contracts?.filter(c => c.created_by === member.name) || [];
            return [...acc, ...memberContracts];
          }, []);
        }          
        
        // Calculate stats using only personal and team contracts
        const combinedContracts = [...personalContracts, ...teamContracts];
        const paidContracts = combinedContracts.filter(c => c.payment_status === 'paid');
        
        const stats = {
          // Only contracts explicitly created by team members
          teamContracts: teamContracts.length,          // Only contracts created by the manager themselves
          personalContracts: personalContracts.length,
          // Pending contracts from both personal and verified team contracts
          pendingContracts: combinedContracts.filter(c => c.payment_status === 'pending').length,
          // Total value from only paid contracts (both personal and team)
          totalValue: paidContracts.reduce((sum, c) => sum + (parseFloat(c.grand_total) || 0), 0)
        };
        
        setContractStats(stats);
      } catch (error) {
        console.error('Error fetching contract stats:', error);
      }
    };

    fetchContractStats();
  }, []);

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 800);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">Loading Dashboard</Typography>
      </Box>
    );
  }

  if (!userData) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', maxWidth: 500, mx: 'auto', mt: 8 }}>
        <Typography variant="h5" color="error" gutterBottom>Session Expired</Typography>
        <Typography variant="body1" color="text.secondary" paragraph>Please log in again to access the dashboard.</Typography>
        <Button variant="contained" href="/login" size="large">Return to Login</Button>
      </Box>
    );
  }
 


  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 6 }}>
      <Container maxWidth={false}  sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: { xs: 'column', md: 'row' }, mb: 4 }}>
          <Box>
            <Typography variant="h4" fontWeight="700" sx={{ mb: 1 }}>{userData.role} Dashboard</Typography>
            <Typography variant="body1" color="text.secondary">Welcome back, {userData.name}</Typography>
          </Box>

          <Stack direction="row" spacing={2} mt={{ xs: 2, md: 0 }}>
            <Button variant="outlined" onClick={handleRefresh} startIcon={<RefreshIcon />} sx={{ textTransform: 'none', height: "fit-content" }}>Refresh</Button>
            {/* <Button variant="contained" startIcon={<PeopleIcon />} sx={{ textTransform: 'none' }}>New Lead</Button> */}
          </Stack>
        </Box>

        <Card elevation={0} sx={{ mb: 4, borderRadius: 2, backgroundImage: 'linear-gradient(to right, #4caf50, #388e3c)', color: 'white' }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography variant="h5" fontWeight="600" sx={{ mb: 1 }}>{userData.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Chip label={userData.role} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Last logout: {userData.last_logout ? new Date(userData.last_logout).toLocaleString() : 'N/A'}
                  </Typography>

                  <Typography variant="body2">{userData.email}</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

      
      

        <Typography variant="h6" fontWeight="600" sx={{ mb: 2, mt: 4, display: 'flex', alignItems: 'center' }}>
          <DashboardIcon sx={{ mr: 1, color: 'primary.main' }} />
          Sales Analytics
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<AssessmentIcon />}
              title="Team Contracts"
              value={contractStats.teamContracts}
              change={8}
              trend="up"
              color="#3b82f6"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<AssessmentIcon />}
              title="Personal Contracts"
              value={contractStats.personalContracts}
              change={5}
              trend="up"
              color="#10b981"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<AssessmentIcon />}
              title="Pending Contracts"
              value={contractStats.pendingContracts}
              change={12}
              trend="up"
              color="#f59e0b"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<AssessmentIcon />}
              title="Total Contract Value"
              value={`$${contractStats.totalValue.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`}
              change={15}
              trend="up"
              color="#ef4444"
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default SalesManagerDashboard;
